import React, { useEffect, useMemo, useState, useRef } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import PropTypes from "prop-types";
import { UIButton } from "../../../../../components/ui";
import {
  fetchImageAsBase64,
  localizationKey,
  localizedCheckInStatus,
  localizedGender,
  localizedStatus,
} from "../../../../../utils/Helpers";
import { getApplicantExportCellValue } from "../../../../../utils/applicantExportNormalize";
import logo from "../../../../../assets/images/yoram-logo-export.png";
import { useSelector } from "react-redux";
import messages from "../../../../../assets/locale/messages";

/** API / normalized keys — matches export column request order, with training grouped and combined total last. */
const EXPORT_APPLICANT_COLUMN_ORDER = [
  "ID",
  "Image URL",
  "Name",
  "Email",
  "Gender",
  "Note",
  "Zone",
  "Check-in Status",
  "Check-in Time",
  "Check-out Time",
  "Status",
  "Stc Number",
  "National Id",
  "Phone Number",
  "Auto Deduction Amount",
  "Manual Deduction Amount",
  "Manual Deduction Reason",
  "Reward Amount",
  "Reward Reason",
  "Daily Amount",
  "Number of Working Days",
  "Role",
  "Supervisor",
  "Date",
  "Number of Training Days",
  "Daily Training Amount",
  "Total Training Amount",
  "Total Shift Amount",
  "Total Amount",
];

const EXPORT_APPLICANT_COLUMN_ORDER_SET = new Set(EXPORT_APPLICANT_COLUMN_ORDER);

function sortExportApplicantHeaderKeys(keys) {
  const keySet = new Set(keys);
  const ordered = EXPORT_APPLICANT_COLUMN_ORDER.filter((k) => keySet.has(k));
  const remainder = keys.filter((k) => !EXPORT_APPLICANT_COLUMN_ORDER_SET.has(k));
  remainder.sort((a, b) => a.localeCompare(b));
  return [...ordered, ...remainder];
}

function ExportSheetAction({
  exportData,
  handleExportModal,
  eventName,
  handleExportAll,
  event,
  onExportComplete,
}) {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const [prolineLogo, setProlineLogo] = useState(null);
  const processedDataRef = useRef(null);
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const date = `${yyyy}-${mm}-${dd}`;

  const border = {
    top: { style: "thin", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } },
  };

  const center = { vertical: "middle", horizontal: "center" };

  const headerFill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "D9E1F2" },
  };

  const uniqueHeader = useMemo(() => {
    const keys = [
      ...new Set(
        (exportData ?? []).flatMap((obj) => Object.keys(obj) || [])
      ),
    ];
    return sortExportApplicantHeaderKeys(keys);
  }, [exportData]);

  const localizedHeaders = useMemo(() => {
    const getKeys = uniqueHeader.map((key) => localizationKey(key));
    return getKeys.map((key) => events.exportApplicants?.[key] || key);
  }, [uniqueHeader, events, lang]);

  const updatedData =
    exportData &&
    exportData.map((item) => ({
      ...item,
      Gender: lang === "ar" ? localizedGender(item.Gender) : item.Gender,
      Status: lang === "ar" ? localizedStatus(item.Status) : item.Status,
      ["Check-in Status"]:
        lang === "ar"
          ? localizedCheckInStatus(item["Check-in Status"])
          : item["Check-in Status"],
    }));

  async function handleDownloadSheet() {
    handleExportAll();
    handleExportModal();
  }

  const handleBufferLogo = async () => {
    const _logo = await fetchImageAsBase64(logo);
    setProlineLogo(_logo);
  };

  const handleExport = async () => {
    if (!prolineLogo) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("applicant_sheet", {
      views: [{ rightToLeft: lang === "ar" }],
    });

    const getColumnLetter = (colNum) => {
      let result = "";
      while (colNum > 0) {
        colNum--;
        result = String.fromCharCode(65 + (colNum % 26)) + result;
        colNum = Math.floor(colNum / 26);
      }
      return result;
    };

    const totalCols = uniqueHeader.length + 1;
    const logoColIndex = totalCols + 1;
    const logoCol = getColumnLetter(logoColIndex);
    const eventNameEndCol = getColumnLetter(totalCols);

    const getColumnWidth = (key) => {
      const keyLower = key?.toLowerCase() || "";
      if (keyLower.includes("email")) {
        return 35;
      } else if (keyLower.includes("name") && !keyLower.includes("image")) {
        return 30;
      } else if (keyLower.includes("zone")) {
        return 40;
      } else if (keyLower.includes("image")) {
        return 60;
      } else if (keyLower.includes("supervisor")) {
        return 30;
      } else if (
        keyLower.includes("reason") &&
        (keyLower.includes("deduction") || keyLower.includes("reward"))
      ) {
        return 45;
      } else if (
        keyLower.includes("total") &&
        (keyLower.includes("amount") || keyLower.includes("shift"))
      ) {
        return 32;
      } else if (keyLower.includes("training")) {
        return 22;
      } else {
        return 20;
      }
    };

    const logoColumnWidth = 25;
    worksheet.columns = [
      { key: "index", width: 5 },
      ...uniqueHeader.map((key) => ({ width: getColumnWidth(key) })),
      { key: "logo", width: logoColumnWidth },
    ];

    worksheet.mergeCells(`${logoCol}1:${logoCol}4`);

    const logoId = workbook.addImage({
      base64: prolineLogo,
      extension: "png",
    });

    const logoColPosition = logoColIndex - 1 + 0.95;

    worksheet.addImage(logoId, {
      tl: { col: logoColPosition, row: 0.5 },
      ext: { width: 130, height: 25 },
    });

    worksheet.mergeCells("A1:B1");
    worksheet.getCell("A1").value =
      events.exportApplicants?.name || shared.lists.name || "Event Name";
    worksheet.mergeCells(`C1:${eventNameEndCol}1`);
    worksheet.getCell("C1").value = eventName || "";

    if (event?.project_managers?.length > 0) {
      const projectManagerName = event.project_managers
        .map((pm) => pm.admin?.name || pm.admin?.email || "")
        .join(", ");
      worksheet.mergeCells("A2:B2");
      worksheet.getCell("A2").value =
        lang === "ar" ? "مدير المشروع" : "Project Manager";
      worksheet.mergeCells(`C2:${eventNameEndCol}2`);
      worksheet.getCell("C2").value = projectManagerName;
    } else {
      worksheet.mergeCells("A2:B2");
      worksheet.getCell("A2").value =
        lang === "ar" ? "مدير المشروع" : "Project Manager";
      worksheet.mergeCells(`C2:${eventNameEndCol}2`);
      worksheet.getCell("C2").value = "";
    }

    worksheet.mergeCells("A3:B3");
    worksheet.getCell("A3").value = lang === "ar" ? "الموقع" : "Location";
    worksheet.mergeCells(`C3:${eventNameEndCol}3`);
    worksheet.getCell("C3").value = event?.address || "";

    worksheet.mergeCells("A4:B4");
    worksheet.getCell("A4").value =
      events.exportApplicants?.date || shared.date || "Date";
    worksheet.mergeCells(`C4:${eventNameEndCol}4`);
    const eventStartDate = event?.event_start_at
      ? new Date(event.event_start_at).toLocaleDateString()
      : "";
    const eventEndDate = event?.event_end_at
      ? new Date(event.event_end_at).toLocaleDateString()
      : "";
    worksheet.getCell("C4").value =
      eventStartDate && eventEndDate
        ? `${eventStartDate} - ${eventEndDate}`
        : date;

    Array.from({ length: 4 }, (_, i) => i + 1).forEach((rowNum) => {
      const row = worksheet.getRow(rowNum);
      row.height = 25;
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = center;
        cell.border = border;
      });
    });

    const headers = ["#", ...localizedHeaders];

    const headerRow = worksheet.addRow(headers);
    headerRow.height = 25;
    headerRow.eachCell((cell) => {
      cell.fill = headerFill;
      cell.font = { bold: true };
      cell.alignment = center;
      cell.border = border;
    });

    updatedData?.forEach((item, index) => {
      const row = worksheet.addRow([]);
      const rowValues = [
        index + 1,
        ...uniqueHeader.map((key) =>
          getApplicantExportCellValue(key, item[key]),
        ),
      ];

      rowValues.forEach((value, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        cell.value = value;
        cell.alignment = center;
        cell.border = border;
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${eventName ? eventName : "event_sheet"}.xlsx`);
  };

  useEffect(() => {
    handleBufferLogo();
  }, []);

  useEffect(() => {
    if (
      exportData?.length > 0 &&
      prolineLogo &&
      processedDataRef.current !== exportData
    ) {
      handleExport().then(() => {
        processedDataRef.current = exportData;
        if (onExportComplete) {
          onExportComplete();
        }
      });
    }
  }, [exportData, prolineLogo, onExportComplete]);

  return (
    <UIButton
      variant="primary"
      size="large"
      appearance="outline"
      onClick={() => handleDownloadSheet()}
    >
      {events.viewEvents.applicantsTab.exportAll}
    </UIButton>
  );
}

ExportSheetAction.propTypes = {
  exportData: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleExportModal: PropTypes.func,
  eventName: PropTypes.string,
  handleExportAll: PropTypes.func,
  event: PropTypes.object,
  onExportComplete: PropTypes.func,
};
export default ExportSheetAction;
